import { Undo2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '$lib/lib/cn';
import { UNDO_DISMISS_MS } from '$lib/hooks/useComparables';
import type { ComparableDTO } from '$lib/api/types';

// Inline undo strip for the most-recent exclusion (DEV-94 decision 10).
// Sits at the bottom of the aggregate panel, NOT a floating toast — analysts
// expect feedback near the source action and toasts in this product would
// violate the "no friendly noise" rule.

export interface UndoStripProps {
  comparable: ComparableDTO;
  onUndo: () => void;
  onDismiss: () => void;
  className?: string;
}

export function UndoStrip({ comparable, onUndo, onDismiss, className }: UndoStripProps) {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      data-testid="undo-strip"
      className={cn(
        'relative flex items-center justify-between gap-3 overflow-hidden rounded-sm border border-rule bg-surface-2 px-3 py-1.5 text-xs',
        className
      )}
    >
      <div className="min-w-0 flex-1 truncate">
        <span className="text-fg-muted">{t('comparables.undo.excluded')} </span>
        <span className="text-fg" title={comparable.address}>
          {comparable.address}
        </span>
      </div>
      <button
        type="button"
        data-testid="undo-button"
        className="inline-flex h-6 items-center gap-1 rounded-xs border border-rule-strong bg-surface px-2 text-xs text-fg hover:bg-surface-3"
        onClick={onUndo}
      >
        <Undo2 size={11} />
        {t('comparables.undo.undo')}
      </button>
      <button
        type="button"
        aria-label={t('comparables.undo.dismiss')}
        className="text-2xs text-fg-subtle hover:text-fg-muted"
        onClick={onDismiss}
      >
        ×
      </button>
      <span
        aria-hidden
        className="undo-countdown absolute bottom-0 left-0 h-px bg-accent"
        style={{
          // Reduced-motion override lives in globals.css and beats this with !important.
          animation: `undo-countdown ${UNDO_DISMISS_MS}ms linear forwards`
        }}
      />
    </div>
  );
}
